import Immutable from 'immutable';

import {
	webapi,
	createModuleState,
	DefinitionUtils,
	EntityDetailModel,
	FormDefinition as Form,
	FormItemDefinition as FormItem,
	CrossDefinition as Cross,
	CentererDefinition as Centerer,
	ButtonDefinition as Button,
	ContextValidator,
	DateUtils,
	REPORTED_EXCEPTION,
	updateEntityTimestamp,
	createModelForState,
	setPreference,
	getPreference
} from 'ps-gui';
import {
	getColumnText,
	getPhrase
} from '../data/phrases';
import { createSearchModel } from './utils';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_STATE = createModuleState('SubmissionAdmin');

export const STATUS_GROUPS = [
	'OPEN',
	'SUBMITTED',
	'CLOSED'
];

export const SUBMISSION_ROLES = [
	'PER',
	'PRO'
];

export default class SubmissionAdminModel {
	#data;

	constructor(data) {
		this.#data = data || EMPTY_MAP;
	}

	#setInitialized() {
		return new SubmissionAdminModel(this.#data.set('initialized', true));
	}

	#setProperty(name, value) {
		if (Array.isArray(name)) {
			return new SubmissionAdminModel(this.#data.setIn(name, value));
		} else {
			return new SubmissionAdminModel(this.#data.set(name, value));
		}
	}

	async setStatusGroupFilter(key, value) {
		return this.#setProperty('statusGroupFilter', (this.#data.get('statusGroupFilter') || EMPTY_MAP).set(key, value)).refreshSearch();
	}

	async setRoleFilter(key, value) {
		return this.#setProperty('roleFilter', (this.#data.get('roleFilter') || EMPTY_MAP).set(key, value)).refreshSearch();
	}

	async checkGlobalContextChange(globalContext) {
		if (!globalContext) {
			return this;
		}
		const gcLangId = globalContext.getLanguageId();
		const langId = this.#data.get('langId');
		if (gcLangId === langId) {
			return this;
		}
		return (await this.initialize(globalContext)).#setProperty('languageTimestamp', new Date().getTime());
	}

	getLanguageTimestamp() {
		return this.#data.get('languageTimestamp') || 0;
	}

	toggleStatusChangeFileDeleted(idx) {
		if (!this.#data.get('statusChangeForm')) {
			return this;
		}
		const path = [
			'statusChangeForm',
			'files',
			idx,
			'_deleted'
		];
		return this.#setProperty(path, !this.#data.getIn(path));
	}

	getDisplayedSubmissionId() {
		return this.#data.getIn(['detail', 'id']);
	}

	closeDetail() {
		return this.#setProperty('detail', null);
	}

	setStatusChangeProperty(name, value) {
		return this.#setProperty(['statusChangeForm', name], value);
	}

	showStatusChangeForm(statusId) {
		return this.#setProperty('statusChangeForm', EMPTY_MAP.set('statusId', statusId).set('submissionId', this.getDisplayedSubmissionId()));
	}

	async applyStatusChange() {
		const res = await webapi.execute('/api/proc/api_submission/setStatus', this.#data.get('statusChangeForm').toJS());
		if (res === REPORTED_EXCEPTION) {
			return this;
		}
		updateEntityTimestamp('submission');
		let model = this.hideStatusChangeForm();
		return await model.reloadDetail();
	}

	hideStatusChangeForm() {
		return this.#setProperty('statusChangeForm', null);
	}

	initializePreferences() {
		let data = this.#data;
		let ef = getPreference(this.#getExtFilterPreferenceKey());
		if (!ef) {
			return this;
		}

		if (ef.get('statusGroupFilter')) {
			data = data.set('statusGroupFilter', ef.get('statusGroupFilter'));
		}
		if (ef.get('roleFilter')) {
			data = data.set('roleFilter', ef.get('roleFilter'));
		}
		return new SubmissionAdminModel(data);
	}

	async initialize(globalContext) {
		console.log('inicializuji podani');
		let srch = createSearchModel('/api/dq/sv_admin_submission').setEntityName('submission').initializePreferences();
		let model = this;
		model = model.#setProperty('statusGroupFilter', Immutable.Map(STATUS_GROUPS.reduce((acc, curr) => {
			acc[curr] = true;
			return acc;
		}, {})));
		model = model.#setProperty('roleFilter', Immutable.Map(SUBMISSION_ROLES.reduce((acc, curr) => {
			acc[curr] = true;
			return acc;
		}, {})));
		model = model.setSearch(srch).#setInitialized();
		model = model.initializePreferences();
		model = await model.refreshSearch(true);
		return model.#setProperty('langId', globalContext.getLanguageId());
	}

	#getPreferenceKeyBase() {
		return '//subm//';
	}

	#getExtFilterPreferenceKey() {
		return this.#getPreferenceKeyBase() + 'extFilter';
	}

	async refreshSearch(dontSavePreference) {

		const statusGroupFilter = this.#data.get('statusGroupFilter') || EMPTY_MAP;
		const roleFilter = this.#data.get('roleFilter') || EMPTY_MAP;

		if (!dontSavePreference) {
			setPreference(this.#getExtFilterPreferenceKey(), Immutable.Map({
				statusGroupFilter,
				roleFilter
			}));
		}

		let fixedFilter = {
			status_group: STATUS_GROUPS.filter(key => statusGroupFilter.get(key)),
			role_type_id: SUBMISSION_ROLES.filter(key => roleFilter.get(key)),
		};

		let srch = this.getSearch().setFixedFilter(fixedFilter);
		srch = await srch.search(1);
		return this.setSearch(srch);
	}

	async reloadDetail() {
		return this.openDetail(this.getDisplayedSubmissionId());
	}

	async openDetail(id) {
		let res = await webapi.execute('/api/proc/api_submission/getSubmissionDetail', {
			id
		});
		if (res === REPORTED_EXCEPTION) {
			return this;
		}
		return this.#setProperty('detail', Immutable.fromJS(res));
	}

	setSearch(model) {
		return new SubmissionAdminModel(this.#data.set('search', model.getState()));
	}

	getSearch() {
		return createModelForState(this.#data.get('search'));
	}

	getData() {
		return this.#data;
	}

	getState() {
		return EMPTY_STATE.set('data', this.#data);
	}
}

function detailReadOnly(ctx) {
	return ctx.getProperty('isDeleted') === 'Y' || ctx.getProperty('isActive') === 'N';
}

let detailDefinition = Centerer(Form().setOpacity(ctx => ctx.getProperty('isDeleted') === 'Y' ? '0.3' : '1').setVirtual(false).setBasePath('detail').setItems([
	Cross().setItems([
		FormItem('fromDate', 'date').setMandatory(true).setWithTime(true).setReadOnly(detailReadOnly).setLabel(getColumnText('maintenanceFrom')),
		FormItem('toDate', 'date').setMandatory(true).setWithTime(true).setReadOnly(detailReadOnly).setLabel(getColumnText('maintenanceTo')),
		FormItem('notificationFromDate', 'date').setMandatory(true).setWithTime(true).setReadOnly(detailReadOnly),
	]).setValidator(ContextValidator((errors, ctx, itemName) => {
		const fromDate = ctx.getProperty('fromDate');
		const toDate = ctx.getProperty('toDate');
		const notifFromDate = ctx.getProperty('notificationFromDate');
		errors.add(
			ctx.getFullPath('toDate'),
			fromDate && toDate && DateUtils.compareDates(fromDate, toDate) >= 0 ? getPhrase('maintenanceToMustBeGreaterThanMaintenanceFrom') :
				fromDate && toDate && DateUtils.compareDates(DateUtils.truncate(fromDate), DateUtils.truncate(toDate)) ? getPhrase('maintenanceFromAndToMustBeSameDay') : null,
			'toDateLteFromDate'
		);
		errors.add(
			ctx.getFullPath('notificationFromDate'),
			fromDate && notifFromDate && DateUtils.compareDates(notifFromDate, fromDate) >= 0 ? getPhrase('notificationFromDateMustBeLessThanMaintenanceFrom') : null,
			'notifDateGteFromDate'
		);
	})),
	FormItem('comments', 'text').setMultiline(true).setReadOnly(ctx => ctx.getProperty('isDeleted') === 'Y'),
	Cross().setItems([Button('delete', getPhrase('delete')).setListener(async e => {
		const ctx = e.ctx;
		let res = await webapi.execute('/api/proc/API_SystemSettings/deleteSubmissionAdmin', {
			id: ctx.getProperty('id')
		});
		if (res === REPORTED_EXCEPTION) {
			return;
		}
		updateEntityTimestamp('systemMaintenance');
		ctx.setModel(await ctx.getModel().reload());
	})]).setJustifyContent('right').setVisible(ctx => ctx.getProperty('isActive') === 'Y' && ctx.getProperty('isDeleted') === 'N')
])).setMaxWidth('800px');

let detailModel = null;

export function getDetailModel() {
	if (!detailModel) {
		DefinitionUtils.setLabels(detailDefinition);

		detailModel = new EntityDetailModel()
			.setLoadUri('/api/proc/api_systemsettings/getSubmissionAdminDetail')
			.setSaveUri('/api/proc/api_systemsettings/saveSubmissionAdmin')
			.setDefinition(detailDefinition)
			.setEntityName('systemMaintenance');
	}
	return detailModel;
}

